import { useState, useCallback, useMemo } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  ConciergeSidebar,
  signOut,
  useConciergeContext,
} from "@brandclub/common-ui";
import { useAppSelector } from "@/redux/hooks";
import "./concierge-tailwind.css";

const ConciergeOutlet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const conversationState = useConciergeContext();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  const sidebarUserProfile = useMemo(
    () => ({
      firstName: userProfile?.given_name,
      lastName: userProfile?.family_name,
      isSignedIn: !!userProfile?.userId,
    }),
    [userProfile]
  );

  const handleConversationSelect = useCallback(
    (conversationId: string) => {
      conversationState.setConversationId(conversationId);
      conversationState.setSharedConversationId(null);
      navigate("/concierge");
    },
    [conversationState, navigate]
  );

  const handleComposeClick = useCallback(() => {
    conversationState.setConversationId(null);
    conversationState.setSharedConversationId(null);
    navigate("/concierge");
  }, [conversationState, navigate]);

  const handleChatsClick = useCallback(() => {
    const isOnHistory = location.pathname.includes("/concierge/history");
    if (isOnHistory) {
      if (!isSidebarExpanded) setIsSidebarExpanded(true);
      return;
    }
    navigate("/concierge/history");
  }, [navigate, location.pathname, isSidebarExpanded]);

  const handleHomeClick = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const handleSignOut = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("conversationId");
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, "", newUrl);
    await signOut();
  }, []);

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarExpanded((prev) => !prev);
  }, []);

  return (
    <div
      className="concierge-root"
      style={{
        display: "flex",
        flexDirection: "row",
        height: "100dvh",
        minHeight: "-webkit-fill-available",
      }}
    >
      <ConciergeSidebar
        onConversationSelect={handleConversationSelect}
        onComposeClick={handleComposeClick}
        onChatsClick={handleChatsClick}
        currentConversationId={conversationState.conversationId}
        userProfile={sidebarUserProfile}
        apiBaseUrl={conversationState.apiBaseUrl}
        onSignOutClick={handleSignOut}
        onHomeClick={handleHomeClick}
        isExpanded={isSidebarExpanded}
        onToggle={handleSidebarToggle}
      />
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Outlet />
      </div>
    </div>
  );
};

export default ConciergeOutlet;
